import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserAuth } from "../../context/userAuthContext";
import { db } from "../../firebase";
import {
    doc,
    updateDoc,
    arrayRemove,
    serverTimestamp,
    deleteDoc
} from "firebase/firestore";
import {
    X,
    ShoppingCart,
    Trash2,
    Plus,
    Minus,
    ArrowRight,
    ShoppingBag,
    AlertTriangle,
    Loader
} from "lucide-react";

const CartSidebar = ({ isOpen, onClose, cartId, cart, placeId, isProcessing, setIsProcessing }) => {
    const navigate = useNavigate();
    const { user } = useUserAuth();
    const [cartItems, setCartItems] = useState([]);
    const [error, setError] = useState("");
    const [cartTotal, setCartTotal] = useState(0);

    // Filtrar items por restaurante actual y calcular totales
    useEffect(() => {
        if (cart && cart.items) {
            const itemsFromCurrentPlace = cart.items.filter(item => item.placeId === placeId);
            setCartItems(itemsFromCurrentPlace);

            // Calcular el total
            const total = itemsFromCurrentPlace.reduce((sum, item) => sum + (item.price * item.quantity), 0);
            setCartTotal(total);
        } else {
            setCartItems([]);
            setCartTotal(0);
        }
    }, [cart, placeId]);

    // Incrementar cantidad de un producto
    const increaseQuantity = async (item) => {
        if (!cartId || isProcessing) return;

        setIsProcessing(true);

        try {
            const cartRef = doc(db, "carts", cartId);

            // Encontrar el ítem en el carrito completo
            const itemIndex = cart.items.findIndex(
                cartItem => cartItem.productId === item.productId && cartItem.placeId === placeId
            );

            if (itemIndex >= 0) {
                // Crear una copia del array de items para modificar
                const updatedItems = [...cart.items];
                updatedItems[itemIndex].quantity += 1;

                await updateDoc(cartRef, {
                    items: updatedItems,
                    updatedAt: serverTimestamp()
                });
            }
        } catch (error) {
            console.error("Error increasing quantity:", error);
            setError("No se pudo aumentar la cantidad. Inténtalo de nuevo.");
        } finally {
            setIsProcessing(false);
        }
    };

    // Disminuir cantidad de un producto
    const decreaseQuantity = async (item) => {
        if (!cartId || isProcessing) return;

        setIsProcessing(true);

        try {
            const cartRef = doc(db, "carts", cartId);

            // Encontrar el ítem en el carrito completo
            const itemIndex = cart.items.findIndex(
                cartItem => cartItem.productId === item.productId && cartItem.placeId === placeId
            );

            if (itemIndex >= 0) {
                const currentQuantity = cart.items[itemIndex].quantity;

                if (currentQuantity > 1) {
                    // Reducir la cantidad
                    const updatedItems = [...cart.items];
                    updatedItems[itemIndex].quantity -= 1;

                    await updateDoc(cartRef, {
                        items: updatedItems,
                        updatedAt: serverTimestamp()
                    });
                } else {
                    // Eliminar el ítem si la cantidad es 1
                    await updateDoc(cartRef, {
                        items: arrayRemove(cart.items[itemIndex]),
                        updatedAt: serverTimestamp()
                    });

                    // Si el carrito quedará vacío, eliminar el documento
                    if (cart.items.length === 1) {
                        await deleteDoc(cartRef);
                    }
                }
            }
        } catch (error) {
            console.error("Error decreasing quantity:", error);
            setError("No se pudo disminuir la cantidad. Inténtalo de nuevo.");
        } finally {
            setIsProcessing(false);
        }
    };

    // Eliminar un producto del carrito
    const removeItem = async (item) => {
        if (!cartId || isProcessing) return;

        setIsProcessing(true);

        try {
            const cartRef = doc(db, "carts", cartId);

            // Encontrar el ítem en el carrito completo
            const cartItem = cart.items.find(
                cartItem => cartItem.productId === item.productId && cartItem.placeId === placeId
            );

            if (cartItem) {
                await updateDoc(cartRef, {
                    items: arrayRemove(cartItem),
                    updatedAt: serverTimestamp()
                });

                // Si el carrito quedará vacío, eliminar el documento
                if (cart.items.length === 1) {
                    await deleteDoc(cartRef);
                }
            }
        } catch (error) {
            console.error("Error removing item:", error);
            setError("No se pudo eliminar el producto. Inténtalo de nuevo.");
        } finally {
            setIsProcessing(false);
        }
    };

    // Vaciar el carrito del restaurante actual
    const clearCart = async () => {
        if (!cartId || isProcessing) return;

        setIsProcessing(true);

        try {
            const cartRef = doc(db, "carts", cartId);

            // Filtrar los items que no pertenecen al restaurante actual
            const remainingItems = cart.items.filter(item => item.placeId !== placeId);

            if (remainingItems.length > 0) {
                // Actualizar el carrito solo con los items de otros restaurantes
                await updateDoc(cartRef, {
                    items: remainingItems,
                    updatedAt: serverTimestamp()
                });
            } else {
                // Si no quedan items, eliminar el documento del carrito
                await deleteDoc(cartRef);
            }

            // Cerrar el carrito si está vacío
            if (remainingItems.length === 0) {
                onClose();
            }
        } catch (error) {
            console.error("Error clearing cart:", error);
            setError("No se pudo vaciar el carrito. Inténtalo de nuevo.");
        } finally {
            setIsProcessing(false);
        }
    };

    // Proceder al checkout
    const proceedToCheckout = () => {
        navigate(`/checkout/${placeId}`);
        onClose();
    };

    // Formatear precio
    const formatPrice = (price) => {
        return `${parseFloat(price).toFixed(2)}`;
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            {/* Overlay */}
            <div
                className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                onClick={onClose}
            />

            {/* Sidebar panel */}
            <div className="fixed inset-y-0 right-0 max-w-full flex">
                <div className="w-screen max-w-md">
                    <div className="h-full flex flex-col bg-white shadow-xl">
                        {/* Header */}
                        <div className="px-4 py-6 bg-purple-700 sm:px-6">
                            <div className="flex items-center justify-between">
                                <h2 className="text-lg font-medium text-white flex items-center">
                                    <ShoppingCart className="mr-2 h-5 w-5" />
                                    Tu Pedido
                                </h2>
                                <button
                                    type="button"
                                    className="rounded-md text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                    onClick={onClose}
                                >
                                    <span className="sr-only">Cerrar</span>
                                    <X className="h-6 w-6" />
                                </button>
                            </div>
                            <div className="mt-1">
                                <p className="text-sm text-purple-200">
                                    {cartItems.length} {cartItems.length === 1 ? 'producto' : 'productos'} en tu carrito
                                </p>
                            </div>
                        </div>

                        {/* Cart content */}
                        <div className="flex-1 overflow-y-auto">
                            {error && (
                                <div className="bg-red-50 p-4 m-4 rounded-md">
                                    <div className="flex">
                                        <div className="flex-shrink-0">
                                            <AlertTriangle className="h-5 w-5 text-red-400" />
                                        </div>
                                        <div className="ml-3">
                                            <p className="text-sm text-red-700">{error}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {cartItems.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full text-center px-4 sm:px-6">
                                    <ShoppingBag className="h-16 w-16 text-gray-300 mb-4" />
                                    <h3 className="text-lg font-medium text-gray-900 mb-1">Tu carrito está vacío</h3>
                                    <p className="text-gray-500 mb-6">
                                        Explora los productos y añade algunos a tu carrito
                                    </p>
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                                    >
                                        Continuar comprando
                                    </button>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-200">
                                    {/* Cart items */}
                                    <ul className="divide-y divide-gray-200">
                                        {cartItems.map((item) => (
                                            <li key={item.productId} className="px-4 py-6 sm:px-6">
                                                <div className="flex items-start">
                                                    {/* Product image */}
                                                    <div className="flex-shrink-0 w-16 h-16 rounded-md overflow-hidden bg-gray-100">
                                                        {item.image ? (
                                                            <img
                                                                src={item.image}
                                                                alt={item.name}
                                                                className="w-full h-full object-cover"
                                                            />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center">
                                                                <ShoppingBag className="h-8 w-8 text-gray-400" />
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Product details */}
                                                    <div className="ml-4 flex-1">
                                                        <div className="flex justify-between">
                                                            <h4 className="text-base font-medium text-gray-900">{item.name}</h4>
                                                            <button
                                                                type="button"
                                                                className="text-gray-400 hover:text-gray-500"
                                                                onClick={() => removeItem(item)}
                                                                disabled={isProcessing}
                                                            >
                                                                <span className="sr-only">Eliminar</span>
                                                                <Trash2 className="h-4 w-4" />
                                                            </button>
                                                        </div>

                                                        {/* Price and quantity */}
                                                        <div className="mt-1 flex justify-between">
                                                            <p className="text-sm text-gray-500">
                                                                Precio unitario: {formatPrice(item.price)}
                                                            </p>
                                                            <p className="text-sm font-medium text-gray-900">
                                                                {formatPrice(item.price * item.quantity)}
                                                            </p>
                                                        </div>

                                                        {/* Quantity controls */}
                                                        <div className="mt-4 flex items-center">
                                                            <button
                                                                type="button"
                                                                onClick={() => decreaseQuantity(item)}
                                                                disabled={isProcessing}
                                                                className="rounded-full p-1 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                                            >
                                                                <span className="sr-only">Quitar uno</span>
                                                                <Minus className="h-4 w-4" />
                                                            </button>
                                                            <span className="text-gray-900 text-sm font-medium mx-3">
                                                                {item.quantity}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={() => increaseQuantity(item)}
                                                                disabled={isProcessing}
                                                                className="rounded-full p-1 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                                            >
                                                                <span className="sr-only">Añadir uno</span>
                                                                <Plus className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>

                                    {/* Clear cart button */}
                                    <div className="px-4 py-4 sm:px-6">
                                        <button
                                            type="button"
                                            onClick={clearCart}
                                            disabled={isProcessing}
                                            className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                                        >
                                            <Trash2 className="h-4 w-4 mr-2" />
                                            Vaciar carrito
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Cart footer */}
                        {cartItems.length > 0 && (
                            <div className="border-t border-gray-200 px-4 py-6 sm:px-6">
                                {/* Cart summary */}
                                <div className="flex justify-between text-base font-medium text-gray-900 mb-4">
                                    <p>Total</p>
                                    <p>{formatPrice(cartTotal)}</p>
                                </div>

                                {/* Checkout button */}
                                <button
                                    type="button"
                                    onClick={proceedToCheckout}
                                    disabled={isProcessing}
                                    className="w-full flex justify-center items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                                >
                                    {isProcessing ? (
                                        <>
                                            <Loader className="animate-spin h-5 w-5 mr-2" />
                                            Procesando...
                                        </>
                                    ) : (
                                        <>
                                            Proceder al Pago
                                            <ArrowRight className="ml-2 h-5 w-5" />
                                        </>
                                    )}
                                </button>

                                <div className="mt-6 flex justify-center text-sm text-center text-gray-500">
                                    <p>
                                        <button
                                            type="button"
                                            className="text-purple-600 font-medium hover:text-purple-500"
                                            onClick={onClose}
                                        >
                                            Continuar comprando
                                            <span aria-hidden="true"> &rarr;</span>
                                        </button>
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CartSidebar;